/**
 * Quick Expense Entry Panel for Future Closure Calculator
 * 
 * Allows users to enter/update monthly expense values directly
 * to maximize claim recovery. Shows source badges and impact preview.
 * 
 * Phase 1 Implementation:
 * - Collapsible section in Future Closure form
 * - Fields: Rent, Utilities, Officer Salary, Insurance, Other Fixed
 * - Live impact preview
 * - Save to report
 */

import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency } from '../utils/formatCurrency';
import { DollarSign, ChevronDown, ChevronUp, Save, AlertCircle, TrendingUp, Info } from 'lucide-react';
import api from '../utils/api';

interface ExpenseData {
  officer_salary: number;
  rent: number;
  utilities: number;
  insurance: number;
  other_fixed: number;
}

interface ExpenseSource {
  officer_salary: 'verified' | 'estimate' | 'template';
  rent: 'verified' | 'estimate' | 'template';
  utilities: 'verified' | 'estimate' | 'template';
  insurance: 'verified' | 'estimate' | 'template';
  other_fixed: 'verified' | 'estimate' | 'template';
}

interface QuickExpenseEntryPanelProps {
  reportId: string;
  reportData?: any;
  closureDurationMonths?: number;
  onExpensesChange?: (expenses: ExpenseData, sources: ExpenseSource) => void;
  onSave?: (expenses: ExpenseData, sources: ExpenseSource) => Promise<void>;
}

// Industry template defaults (BLS median values as % of revenue)
const INDUSTRY_DEFAULTS: Record<string, { 
  officer_salary: number; 
  rent: number; 
  utilities: number; 
  insurance: number; 
  other_fixed: number 
}> = {
  restaurant: { officer_salary: 0.08, rent: 0.08, utilities: 0.025, insurance: 0.015, other_fixed: 0.05 },
  retail: { officer_salary: 0.06, rent: 0.10, utilities: 0.02, insurance: 0.012, other_fixed: 0.04 },
  manufacturing: { officer_salary: 0.05, rent: 0.06, utilities: 0.04, insurance: 0.02, other_fixed: 0.06 },
  service: { officer_salary: 0.10, rent: 0.07, utilities: 0.02, insurance: 0.015, other_fixed: 0.04 },
  healthcare: { officer_salary: 0.12, rent: 0.08, utilities: 0.025, insurance: 0.03, other_fixed: 0.05 },
  default: { officer_salary: 0.07, rent: 0.08, utilities: 0.025, insurance: 0.015, other_fixed: 0.05 }
};

const SOURCE_BADGES: Record<string, { label: string; color: string; icon: string }> = {
  verified: { label: 'Verified', color: 'bg-green-100 text-green-800 border-green-200', icon: '🟢' },
  estimate: { label: 'Client Estimate', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: '🟡' },
  template: { label: 'Industry Default', color: 'bg-gray-100 text-gray-600 border-gray-200', icon: '⚪' }
};

export default function QuickExpenseEntryPanel({
  reportId,
  reportData,
  closureDurationMonths = 36,
  onExpensesChange,
  onSave
}: QuickExpenseEntryPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expenses, setExpenses] = useState<ExpenseData>({
    officer_salary: 0,
    rent: 0,
    utilities: 0,
    insurance: 0,
    other_fixed: 0
  });
  const [sources, setSources] = useState<ExpenseSource>({
    officer_salary: 'template',
    rent: 'template',
    utilities: 'template',
    insurance: 'template',
    other_fixed: 'template'
  });
  const [originalExpenses, setOriginalExpenses] = useState<ExpenseData | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Calculate average monthly revenue from report data
  const getAverageMonthlyRevenue = useCallback(() => {
    if (!reportData?.financial_data) return 50000; // Default fallback
    
    let totalRevenue = 0;
    let monthCount = 0;
    
    const financialData = reportData.financial_data;
    for (const key in financialData) {
      if (key.startsWith('data_') && Array.isArray(financialData[key])) {
        for (const month of financialData[key]) {
          totalRevenue += month.revenue || 0;
          monthCount++;
        }
      }
    }
    
    return monthCount > 0 ? totalRevenue / monthCount : 50000;
  }, [reportData]);

  // Extract current expenses from report data
  const extractCurrentExpenses = useCallback(() => {
    if (!reportData?.financial_data) return null;
    
    let totalOfficer = 0;
    let totalRent = 0;
    let totalUtilities = 0;
    let totalInsurance = 0;
    let totalOther = 0;
    let monthCount = 0;
    
    const financialData = reportData.financial_data;
    for (const key in financialData) {
      if (key.startsWith('data_') && Array.isArray(financialData[key])) {
        for (const month of financialData[key]) {
          // Extract payroll
          const payroll = month.payroll || {};
          totalOfficer += payroll.officer_comp || payroll.officer_salary || 0;
          
          // Extract expenses
          const exp = month.expenses || {};
          totalRent += exp.Rent || exp.rent || 0;
          totalUtilities += exp.Utilities || exp.utilities || 0;
          totalInsurance += exp.Insurance || exp.insurance || 0;
          totalOther += exp['Other Expenses'] || exp.other_expenses || exp.Other || 0;
          
          monthCount++;
        }
      }
    }
    
    if (monthCount === 0) return null;
    
    return {
      officer_salary: Math.round(totalOfficer / monthCount),
      rent: Math.round(totalRent / monthCount),
      utilities: Math.round(totalUtilities / monthCount),
      insurance: Math.round(totalInsurance / monthCount),
      other_fixed: Math.round(totalOther / monthCount)
    };
  }, [reportData]);

  // Initialize expenses from report data or templates
  useEffect(() => {
    const currentExpenses = extractCurrentExpenses();
    const avgRevenue = getAverageMonthlyRevenue();
    const businessType = (reportData?.business_type || 'default').toLowerCase();
    const defaults = INDUSTRY_DEFAULTS[businessType] || INDUSTRY_DEFAULTS.default;
    
    // Calculate template-based defaults
    const templateExpenses: ExpenseData = {
      officer_salary: Math.round(avgRevenue * defaults.officer_salary),
      rent: Math.round(avgRevenue * defaults.rent),
      utilities: Math.round(avgRevenue * defaults.utilities),
      insurance: Math.round(avgRevenue * defaults.insurance),
      other_fixed: Math.round(avgRevenue * defaults.other_fixed)
    };
    
    // Use current data if available, otherwise use templates
    const initialExpenses: ExpenseData = {
      officer_salary: currentExpenses?.officer_salary || templateExpenses.officer_salary,
      rent: currentExpenses?.rent || templateExpenses.rent,
      utilities: currentExpenses?.utilities || templateExpenses.utilities,
      insurance: currentExpenses?.insurance || templateExpenses.insurance,
      other_fixed: currentExpenses?.other_fixed || templateExpenses.other_fixed
    };
    
    // Set sources based on whether data exists
    const initialSources: ExpenseSource = {
      officer_salary: currentExpenses?.officer_salary ? 'estimate' : 'template',
      rent: currentExpenses?.rent ? 'estimate' : 'template',
      utilities: currentExpenses?.utilities ? 'estimate' : 'template',
      insurance: currentExpenses?.insurance ? 'estimate' : 'template',
      other_fixed: currentExpenses?.other_fixed ? 'estimate' : 'template'
    };
    
    setExpenses(initialExpenses);
    setSources(initialSources);
    setOriginalExpenses(initialExpenses);
  }, [reportData, extractCurrentExpenses, getAverageMonthlyRevenue]);

  // Calculate impact when expenses change
  const calculateTotalMonthlyExpenses = () => {
    return expenses.officer_salary + expenses.rent + expenses.utilities + 
           expenses.insurance + expenses.other_fixed;
  };

  const calculateTotalClaimImpact = () => {
    return calculateTotalMonthlyExpenses() * closureDurationMonths;
  };

  const calculateChangeFromOriginal = () => {
    if (!originalExpenses) return 0;
    const originalTotal = originalExpenses.officer_salary + originalExpenses.rent + 
                          originalExpenses.utilities + originalExpenses.insurance + 
                          originalExpenses.other_fixed;
    const newTotal = calculateTotalMonthlyExpenses();
    return (newTotal - originalTotal) * closureDurationMonths;
  };

  // Handle expense change
  const handleExpenseChange = (field: keyof ExpenseData, value: string) => {
    const numValue = parseFloat(value) || 0;
    const newExpenses = { ...expenses, [field]: numValue };
    const newSources = { ...sources, [field]: 'estimate' as const };
    
    setExpenses(newExpenses);
    setSources(newSources);
    setHasUnsavedChanges(true);
    
    if (onExpensesChange) {
      onExpensesChange(newExpenses, newSources);
    }
  };

  // Upgrade source to verified
  const handleUpgradeToVerified = (field: keyof ExpenseSource) => {
    const newSources = { ...sources, [field]: 'verified' as const };
    setSources(newSources);
    setHasUnsavedChanges(true);
  };

  // Apply industry template (fetch from backend)
  const handleApplyTemplate = async () => {
    const avgRevenue = getAverageMonthlyRevenue();
    const businessType = (reportData?.business_type || 'default').toLowerCase().replace(/\s+/g, '_');
    
    try {
      // Try to fetch from backend first
      const data = await api.get(
        `/api/v1/templates/${businessType}/future-closure-defaults?monthly_revenue=${avgRevenue}`,
        { showErrorToast: false }
      );
      
      const templateExpenses: ExpenseData = {
        officer_salary: data.monthly_amounts.officer_salary,
        rent: data.monthly_amounts.rent,
        utilities: data.monthly_amounts.utilities,
        insurance: data.monthly_amounts.insurance,
        other_fixed: data.monthly_amounts.other_fixed
      };
      
      setExpenses(templateExpenses);
      setSources({
        officer_salary: 'template',
        rent: 'template',
        utilities: 'template',
        insurance: 'template',
        other_fixed: 'template'
      });
      setHasUnsavedChanges(true);
      
      console.log(`✅ Applied ${data.template_name} defaults from ${data.source}`);
      
      if (onExpensesChange) {
        onExpensesChange(templateExpenses, {
          officer_salary: 'template',
          rent: 'template',
          utilities: 'template',
          insurance: 'template',
          other_fixed: 'template'
        });
      }
      return;
    } catch (err) {
      console.warn('Failed to fetch template defaults from backend, using local fallback');
    }
    
    // Fallback to local defaults
    const defaults = INDUSTRY_DEFAULTS[businessType] || INDUSTRY_DEFAULTS.default;
    
    const templateExpenses: ExpenseData = {
      officer_salary: Math.round(avgRevenue * defaults.officer_salary),
      rent: Math.round(avgRevenue * defaults.rent),
      utilities: Math.round(avgRevenue * defaults.utilities),
      insurance: Math.round(avgRevenue * defaults.insurance),
      other_fixed: Math.round(avgRevenue * defaults.other_fixed)
    };
    
    setExpenses(templateExpenses);
    setSources({
      officer_salary: 'template',
      rent: 'template',
      utilities: 'template',
      insurance: 'template',
      other_fixed: 'template'
    });
    setHasUnsavedChanges(true);
    
    if (onExpensesChange) {
      onExpensesChange(templateExpenses, {
        officer_salary: 'template',
        rent: 'template',
        utilities: 'template',
        insurance: 'template',
        other_fixed: 'template'
      });
    }
  };

  // Save expenses
  const handleSave = async () => {
    if (!onSave) return;
    
    setSaving(true);
    setSaveMessage(null);
    
    try {
      await onSave(expenses, sources);
      setSaveMessage('Expenses saved successfully!');
      setHasUnsavedChanges(false);
      setOriginalExpenses(expenses);
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setSaveMessage('Failed to save expenses');
    } finally {
      setSaving(false);
    }
  };

  const renderSourceBadge = (source: 'verified' | 'estimate' | 'template', field: keyof ExpenseSource) => {
    const badge = SOURCE_BADGES[source];
    return (
      <div className="flex items-center gap-1">
        <span className={`text-xs px-2 py-0.5 rounded-full border ${badge.color}`}>
          {badge.icon} {badge.label}
        </span>
        {source !== 'verified' && (
          <button
            onClick={() => handleUpgradeToVerified(field)}
            className="text-xs text-blue-600 hover:text-blue-800 underline"
            title="Mark as verified with documentation"
          >
            Verify
          </button>
        )}
      </div>
    );
  };

  const changeFromOriginal = calculateChangeFromOriginal();
  const totalClaimImpact = calculateTotalClaimImpact();

  return (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-green-100/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-bold text-green-900">
              💡 Maximize Your Claim - Enter Actual Expenses
            </h3>
            <p className="text-sm text-green-700">
              Continuing expenses like rent and officer salary increase recoverable loss
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {totalClaimImpact > 0 && (
            <div className="text-right">
              <p className="text-xs text-green-600">Potential Claim Impact</p>
              <p className="text-lg font-bold text-green-700">{formatCurrency(totalClaimImpact)}</p>
            </div>
          )}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-green-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-green-600" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 pb-6 space-y-4">
          {/* Info Banner */}
          <div className="bg-white/60 border border-green-200 rounded-lg p-3 flex items-start gap-2">
            <Info className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-green-800">
              <strong>Tip:</strong> Enter monthly expense amounts. These are fixed costs that continue during closure 
              and are recoverable under business interruption policies. Higher documented expenses = higher claim value.
            </p>
          </div>

          {/* Quick Apply Template */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Business Type: <strong className="text-gray-900">{reportData?.business_type || 'General'}</strong>
            </span>
            <button
              onClick={handleApplyTemplate}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium underline"
            >
              Apply Industry Defaults (BLS Median)
            </button>
          </div>

          {/* Expense Entry Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Officer/Owner Salary */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Officer/Owner Salary</label>
                {renderSourceBadge(sources.officer_salary, 'officer_salary')}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={expenses.officer_salary || ''}
                  onChange={(e) => handleExpenseChange('officer_salary', e.target.value)}
                  placeholder="0"
                  className="w-full pl-8 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">/mo</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Owner/officer compensation that continues during closure</p>
            </div>

            {/* Rent */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Monthly Rent</label>
                {renderSourceBadge(sources.rent, 'rent')}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={expenses.rent || ''}
                  onChange={(e) => handleExpenseChange('rent', e.target.value)}
                  placeholder="0"
                  className="w-full pl-8 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">/mo</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Fixed lease payments required during closure</p>
            </div>

            {/* Utilities */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Utilities</label>
                {renderSourceBadge(sources.utilities, 'utilities')}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={expenses.utilities || ''}
                  onChange={(e) => handleExpenseChange('utilities', e.target.value)}
                  placeholder="0"
                  className="w-full pl-8 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">/mo</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Minimum utility costs to maintain property</p>
            </div>

            {/* Insurance */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Insurance</label>
                {renderSourceBadge(sources.insurance, 'insurance')}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={expenses.insurance || ''}
                  onChange={(e) => handleExpenseChange('insurance', e.target.value)}
                  placeholder="0"
                  className="w-full pl-8 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">/mo</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Property & liability insurance premiums</p>
            </div>

            {/* Other Fixed Expenses */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Other Fixed Expenses</label>
                {renderSourceBadge(sources.other_fixed, 'other_fixed')}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={expenses.other_fixed || ''}
                  onChange={(e) => handleExpenseChange('other_fixed', e.target.value)}
                  placeholder="0"
                  className="w-full pl-8 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">/mo</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">Loan payments, equipment leases, software subscriptions, etc.</p>
            </div>
          </div>

          {/* Impact Preview */}
          <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-4 border border-green-300">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <h4 className="font-semibold text-green-900">Impact Preview ({closureDurationMonths} months)</h4>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-green-600 mb-1">Monthly Continuing Expenses</p>
                <p className="text-xl font-bold text-green-800">
                  {formatCurrency(calculateTotalMonthlyExpenses())}
                </p>
              </div>
              <div>
                <p className="text-xs text-green-600 mb-1">Total Claim Impact</p>
                <p className="text-xl font-bold text-green-800">
                  {formatCurrency(totalClaimImpact)}
                </p>
              </div>
              <div>
                <p className="text-xs text-green-600 mb-1">Change from Current</p>
                <p className={`text-xl font-bold ${changeFromOriginal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {changeFromOriginal >= 0 ? '+' : ''}{formatCurrency(changeFromOriginal)}
                </p>
              </div>
            </div>
            
            {changeFromOriginal > 0 && (
              <div className="mt-3 p-2 bg-green-200/50 rounded-lg">
                <p className="text-sm text-green-800">
                  🎯 <strong>Adding these expenses increases your demand by {formatCurrency(changeFromOriginal)}</strong>
                </p>
              </div>
            )}
          </div>

          {/* Save Button */}
          {onSave && (
            <div className="flex items-center justify-between">
              {saveMessage && (
                <p className={`text-sm ${saveMessage.includes('Failed') ? 'text-red-600' : 'text-green-600'}`}>
                  {saveMessage}
                </p>
              )}
              <div className="flex items-center gap-3 ml-auto">
                {hasUnsavedChanges && (
                  <span className="text-sm text-amber-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    Unsaved changes
                  </span>
                )}
                <button
                  onClick={handleSave}
                  disabled={saving || !hasUnsavedChanges}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold transition-all ${
                    saving || !hasUnsavedChanges
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700 shadow-lg hover:shadow-xl'
                  }`}
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Apply & Recalculate'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
